import { useEffect, useState } from "react";
import { Filter, Undo2, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../config/constants";
import PageTitle from "../components/common/PageTitle";
import DataTable from "../components/common/DataTable";
import FilterDropdown from "../components/common/FilterDropdown";
import SearchBar from "../components/common/SearchBar";
import MobileFilterPanel from "../components/common/MobileFilterPanel";
import ExportExcelModal from "../components/modals/ExportExcelModal";
import { showError } from "../utils/toast";
import { API_BASE_URL } from "../api/apiClient";
import { getProjectName } from "../utils/projectHelpers";
import { JWT } from "../config/constants";

/**
 * VA/VE Dashboard Component (Auth Required-ubah backend nya dong !!!)
 *
 * Read-only table view of all VA/VE data with filtering, sorting, and search.
 * Designed to match LessonDashboard layout.
 */
export default function VaVeDashboard() {
  const navigate = useNavigate();

  const [vaves, setVaves] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  // Mobile filter panel state
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Filter & Sort state
  const [selectedProject, setSelectedProject] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Export state
  const [showExportModal, setShowExportModal] = useState(false);

  // Temporary filter state for mobile
  const [tempProject, setTempProject] = useState("");
  const [tempSort, setTempSort] = useState("newest");

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVaves, setTotalVaves] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem(JWT.TOKEN_KEY);

    if (!token) {
      navigate(ROUTES.LOGIN);
      return;
    }
  }, [navigate]);

  useEffect(() => {
    loadProjects();

    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchKeyword(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    fetchVaVes();
  }, [page, selectedProject, sortBy, searchKeyword]);

  async function loadProjects() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/projects`);
      const data = await response.json();
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load projects:", err);
    }
  }

  async function fetchVaVes() {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (selectedProject) params.set("project_id", selectedProject);
      if (searchKeyword) params.set("search", searchKeyword);
      if (sortBy) params.set("sort_by", sortBy);

      const response = await fetch(
        `${API_BASE_URL}/api/public/vave?${params.toString()}`
      );
      const data = await response.json();

      if (!data) {
        // API returned null (no data for this filter)
        setVaves([]);
        setTotalVaves(0);
        setTotalPages(1);
      } else if (Array.isArray(data)) {
        setVaves(data);
        setTotalVaves(data.length);
        setTotalPages(1);
      } else {
        setVaves(data.data || []);
        setTotalVaves(data.total || 0);
        setTotalPages(data.total_pages || 1);
      }
    } catch (err) {
      console.error("Failed to load VA/VE:", err);
      showError("Gagal memuat data VA/VE");
      setVaves([]);
      setTotalVaves(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }

  function handleProjectChange(val) {
    if (isMobile) setTempProject(val);
    else {
      setSelectedProject(val);
      setPage(1);
    }
  }

  function handleSortChange(val) {
    if (isMobile) setTempSort(val);
    else {
      setSortBy(val);
      setPage(1);
    }
  }

  function handleApplyFilters() {
    setSelectedProject(tempProject);
    setSortBy(tempSort);
    setPage(1);
    setShowMobileFilters(false);
  }

  function handleResetFilters() {
    setTempProject("");
    setTempSort("newest");
  }

  const columns = [
    {
      field: "area_item",
      label: "Area / Item",
      sortable: false,
      className: "min-w-[180px]",
    },
    {
      field: "existing_design",
      label: "Existing Design",
      sortable: false,
      className: "min-w-[150px]",
    },
    {
      field: "usulan_vave",
      label: "Usulan VA/VE",
      sortable: false,
      className: "min-w-[150px]",
    },
    {
      field: "benefit",
      label: "Manfaat",
      sortable: false,
      className: "min-w-[150px]",
    },
    {
      field: "risk_mitigation",
      label: "Risiko & Mitigasi",
      sortable: false,
      className: "min-w-[150px]",
    },
    {
      field: "project",
      label: "Proyek",
      sortable: false,
      className: "min-w-[150px]",
    },
  ];

  return (
    <div className="p-6 pt-20 max-w-[1400px] mx-auto overflow-x-hidden">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-5">
        <div>
          <button
            onClick={() => navigate(ROUTES.PUBLIC_PORTAL)}
            className="flex items-center text-sm gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <Undo2 size={14} />
            Kembali ke Portal Publik
          </button>
          <PageTitle>Dashboard VA/VE</PageTitle>
        </div>
        <button
          onClick={() => setShowExportModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Download size={18} />
          <span>Export Excel</span>
        </button>
      </div>

      {/* STATS + FILTERS ROW */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {/* LEFT COLUMN: Stats Cards + Search/Filters */}
        <div className="flex flex-col gap-3">
          {/* Overview Stat Cards */}
          <div className="flex gap-3">
            <div className="flex-1 flex flex-col gap-1 px-4 py-3 border rounded-lg bg-white shadow-sm">
              <p className="text-xs text-gray-500">Total VA/VE</p>
              <p className="font-bold text-2xl text-gray-800">
                {totalVaves}
              </p>
            </div>
            <div className="flex-1 flex flex-col gap-1 px-4 py-3 border rounded-lg bg-white shadow-sm">
              <p className="text-xs text-gray-500">Proyek</p>
              <p className="font-bold text-2xl text-gray-800">
                {projects.length}
              </p>
            </div>
          </div>

          {/* Search Row */}
          <div className="flex gap-2">
            <div className="flex-1">
              <SearchBar
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Cari VA/VE..."
              />
            </div>
            {isMobile && (
              <button
                onClick={() => setShowMobileFilters(true)}
                className="flex items-center gap-2 px-3 py-2 border bg-white border-gray-300 rounded-lg text-sm"
              >
                <Filter size={16} />
                Filter
              </button>
            )}
          </div>

          {/* Filter Dropdowns Row (desktop only) */}
          {!isMobile && (
            <div className="flex flex-wrap gap-2">
              <div className="w-44">
                <FilterDropdown
                  label="Project"
                  options={[
                { value: "", label: "Semua Project" },
                ...(Array.isArray(projects) ? projects : []).map((proj) => ({
                  value: proj.id,
                  label: proj.division ? (
                  <span>
                    {proj.name}{" "}
                    <span className="text-xs font-medium text-gray-400">
                      ({proj.division})
                    </span>
                  </span>
                ) : (
                  proj.name
                )
                })),
              ]}
                  value={selectedProject}
                  onChange={handleProjectChange}
                  placeholder="Pilih Project"
                />
              </div>
              <div className="w-36">
                <FilterDropdown
                  label="Urutkan"
                  options={[
                    { value: "newest", label: "Terbaru" },
                    { value: "oldest", label: "Terlama" },
                  ]}
                  value={sortBy}
                  onChange={handleSortChange}
                />
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN: Per Proyek breakdown */}
        <div className="flex flex-col md:flex-row gap-4 items-start">
          <div className="w-full md:flex-1 bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden flex flex-col md:h-[180px] md:max-h-[180px]">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2.5">
              <p className="text-white text-xs font-semibold tracking-wide">Jumlah per Proyek</p>
            </div>
            <div className="p-3 flex-1 overflow-y-auto">
              {projects.length > 0 ? (() => {
                // Group by project
                const perProject = {};
                
                // Initialize all projects with 0 count
                projects.forEach(p => {
                  const name = p.name || "Unknown";
                  const division = p.division || "";
                  const key = `${name}|||${division}`;
                  perProject[key] = 0;
                });

                // Count from current VAVE data
                vaves.forEach((v) => {
                  const name = v.project_name || "Unknown";
                  const division = v.project_division || "";
                  const key = `${name}|||${division}`;
                  if (perProject[key] !== undefined) {
                    perProject[key] += 1;
                  } else {
                    perProject[key] = 1;
                  }
                });

                // Convert to array and sort descending by count
                const sortedProjects = Object.entries(perProject)
                  .map(([key, count]) => {
                    const [name, division] = key.split("|||");
                    return { name, division, count };
                  })
                  .sort((a, b) => b.count - a.count);

                return (
                  <div className="space-y-2">
                    {sortedProjects.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
                          <span className="text-xs text-gray-700 truncate max-w-[140px]">{item.name}</span>
                          {item.division && <span className="text-xs text-gray-400">({item.division})</span>}
                        </div>
                        <span className="text-xs font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full min-w-[28px] text-center">
                          {item.count}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })() : (
                <p className="text-xs text-gray-400 text-center py-2">Belum ada data proyek</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MOBILE FILTER PANEL (controlled mode — trigger button is in the search row above) */}
      <MobileFilterPanel
        isOpen={showMobileFilters}
        onToggle={(val) => setShowMobileFilters(typeof val === 'boolean' ? val : !showMobileFilters)}
        filters={[
          {
            label: "Project",
            options: [
              { value: "", label: "Semua Project" },
              ...(Array.isArray(projects) ? projects : []).map((proj) => ({
                value: proj.id,
                label: proj.division ? `${proj.name} (${proj.division})` : proj.name,
              })),
            ],
            value: tempProject,
            onChange: (val) => setTempProject(val),
            placeholder: "Pilih Project",
          },
          {
            label: "Urutkan",
            options: [
              { value: "newest", label: "Terbaru" },
              { value: "oldest", label: "Terlama" },
            ],
            value: tempSort,
            onChange: (val) => setTempSort(val),
          },
        ]}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
      />

      {/* INFO */}
      <div className="mb-4">
        <p className="text-gray-600">
          Menampilkan {vaves.length} dari {totalVaves} VA/VE
        </p>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={vaves}
        loading={loading}
        renderRow={(item) => (
          <tr
            key={item.id}
            className="hover:bg-gray-50 hover:cursor-pointer"
          >
            <td className="px-6 py-4">
              <span className="text-gray-900 font-medium">{item.area_item}</span>
            </td>
            <td className="px-6 py-4">
              <span className="text-sm text-gray-600 line-clamp-2">
                {item.existing_design || "-"}
              </span>
            </td>
            <td className="px-6 py-4">
              <span className="text-sm text-gray-600 line-clamp-2">
                {item.usulan_vave || "-"}
              </span>
            </td>
            <td className="px-6 py-4">
              <span className="text-sm text-gray-600 line-clamp-2">
                {item.benefit || "-"}
              </span>
            </td>
            <td className="px-6 py-4">
              <span className="text-sm text-gray-600 line-clamp-2">
                {item.risk_mitigation || "-"}
              </span>
            </td>
            <td className="px-6 py-4">
              <div>
                <span className="text-sm text-gray-600">
                  {item.project_name ||
                    getProjectName(projects, item.project_id) ||
                    "-"}
                </span>
                {item.project_division && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {item.project_division}
                  </p>
                )}
              </div>
            </td>
          </tr>
        )}
        emptyMessage="Belum ada data VA/VE tersedia."
        pagination={{
          page,
          totalPages,
          onPageChange: setPage,
        }}
      />

      {/* Export Modal */}
      <ExportExcelModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        projects={projects}
        exportType="vave"
        isPublic
      />
    </div>
  );
}
